import { useEffect, useState } from "react";
import { LogOut, Check, X, Crown, Sparkles, User as UserIcon, ShieldCheck } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DemoBadge } from "@/components/Badges";
import { toast } from "sonner";

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/subscriptions/plans").then(({ data }) => setPlans(data.plans));
  }, []);

  const selectPlan = async (planId) => {
    setBusy(planId);
    try {
      await api.post("/subscriptions/select", { plan_id: planId });
      await refresh();
      toast.success(`Switched to ${planId.toUpperCase()} (demo — no payment taken)`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-8 sw-fade-up">
      <header>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-white">Profile</h1>
      </header>

      {/* user card */}
      <div className="flex items-center gap-4 rounded-2xl border border-[#222A38] bg-[#131822] p-5">
        <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-cyan-400 to-violet-500 font-display text-2xl font-extrabold text-[#0B0E14]">
          {user?.name?.[0]?.toUpperCase() || "U"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="truncate font-display text-xl font-bold text-white">{user?.name}</h2>
            {user?.role === "admin" && <ShieldCheck size={16} className="text-cyan-400" />}
          </div>
          <p className="truncate text-sm text-slate-400">{user?.email}</p>
          <span className="mt-1 inline-block rounded-full bg-cyan-500/10 px-2 py-0.5 font-mono-sw text-[10px] uppercase tracking-wider text-cyan-400">
            {user?.plan} plan
          </span>
        </div>
      </div>

      {/* subscription */}
      <section>
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown size={16} className="text-amber-400" />
            <h2 className="font-display text-lg font-bold text-white">Subscription</h2>
          </div>
          <DemoBadge label="NO PAYMENTS" />
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {plans.map((p) => {
            const current = user?.plan === p.id;
            return (
              <div
                key={p.id}
                data-testid={`plan-${p.id}`}
                className={`relative flex flex-col rounded-2xl border p-5 ${
                  p.highlight ? "border-cyan-500/50 bg-cyan-500/5" : "border-[#222A38] bg-[#131822]"
                }`}
              >
                {p.highlight && (
                  <span className="absolute -top-2.5 left-5 flex items-center gap-1 rounded-full bg-cyan-500 px-2 py-0.5 font-mono-sw text-[10px] font-bold uppercase text-[#0B0E14]">
                    <Sparkles size={10} /> Popular
                  </span>
                )}
                <h3 className="font-display text-lg font-bold text-white">{p.name}</h3>
                <p className="text-xs text-slate-400">{p.tagline}</p>
                <div className="mt-3 font-display text-3xl font-extrabold text-white">
                  ${p.price}
                  <span className="text-sm font-medium text-slate-500">/{p.period}</span>
                </div>
                <ul className="mt-4 flex-1 space-y-1.5 text-sm">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-slate-300">
                      <Check size={15} className="mt-0.5 shrink-0 text-emerald-400" /> {f}
                    </li>
                  ))}
                  {p.not_included?.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-slate-600">
                      <X size={15} className="mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`select-plan-${p.id}`}
                  disabled={current || busy === p.id}
                  onClick={() => selectPlan(p.id)}
                  className={`mt-5 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${
                    current
                      ? "cursor-default bg-[#1A202C] text-slate-500"
                      : p.highlight
                      ? "bg-cyan-500 text-[#0B0E14] hover:bg-cyan-400"
                      : "bg-[#1A202C] text-white hover:bg-[#222A38]"
                  }`}
                >
                  {current ? "Current plan" : busy === p.id ? "Switching…" : "Choose plan"}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      <button
        data-testid="logout-btn"
        onClick={logout}
        className="flex items-center justify-center gap-2 rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm font-semibold text-red-300 transition-colors hover:bg-red-500/10"
      >
        <LogOut size={16} /> Sign out
      </button>
    </div>
  );
}
